import React, { useEffect, useState } from 'react'

import { FieldData } from 'rc-field-form/lib/interface'
import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { APP_DEFAULT_STATE } from '@api/local'
import { IconSelectButtonGroup, IconSelectOption } from '@atoms/buttons/IconSelectButtonGroup'
import { IconEnum } from '@atoms/images/Icon'
import { Button, Link, Spacer, Paragraph } from '@atoms/index'
import { ResponsivePXValue, theme } from '@client/components/Theme'
import { useConfig } from '@client/contexts/ConfigProvider'
import { SiteHelper } from '@client/lib/SiteHelper'
import {
  LocationFragment,
  UserAddressFragment, UserCartDocument, UserDetailsDocument, useAddLocationMutation,
  useGetAppQuery,
  useRemoveUserAddressMutation, useUpdateLocationMutation, useUpdateUserAddressMutation, useUserDetailsQuery,
} from '@hooks/api'
import { Form, TextInput, TextAreaInput, useForm } from '@molecules/index'
import { PlaceIdLookup } from '@organisms/index'
import { ConfirmDeleteModal } from '@organisms/modals'
import { BuildingTypeEnum, DeviceTypeEnum } from '@uctypes/api/globalTypes'

import { Coordinates } from '../misc/DraggableMap'
import type { PlaceIdOption } from '../misc/PlaceIdLookup'

const Container = styled.div`

 align-self: stretch;

  ${ResponsivePXValue('margin', '0 16px')}

  .submit-button {
    ${ResponsivePXValue('margin', '16px 0')}
  }
`

const ActionContainer = styled.div<{ $stickyBottom?: boolean, $variant?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props) => props.$stickyBottom && `
    position: sticky;
    ${ResponsivePXValue('bottom', '8px')};
  `}

  ${(props) => props.$variant === 'horizontal' && `
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
  `}

  .address-action-button {
    ${ResponsivePXValue('width', { mobile: '100%', tablet: '180px', desktop: '180px' })};
  }
`

interface EditAddressFormData {
  alias: string
  company: string
  buildingTypeEnum: BuildingTypeEnum | null
  buildingName: string | null
  unitNumber: string | null
  instructions: string
  streetName: string | null
  locationName: string | null
  streetNumber: string | null
}

export interface EditAddressFunctionProps {
  placeId: string
  placeCoordinates: Coordinates
  buildingTypeEnum: BuildingTypeEnum
  locationName: string
  streetName: string
  streetNumber: string
  alias?: string
  company?: string
  buildingName?: string
  unitNumber?: string
  instructions?: string
}

export interface EditAddressFormProps {
  userAddress: UserAddressFragment
  onSuccess: () => void

  onClose: () => void
  onError: (message: string) => void
}

interface EditAddressFormState {
  loading: boolean
  buildingTypeEnum: BuildingTypeEnum
  trigger: boolean
  confirmDeleteModalOpen: boolean
  placeCoordinates: Coordinates | null
  types: string[]
  locationId?: string
  newPlaceId: string | null
}

const DEFAULT_STATE: EditAddressFormState = {
  loading: false,
  trigger: false,
  confirmDeleteModalOpen: false,
  buildingTypeEnum: BuildingTypeEnum.HOUSE,
  placeCoordinates: null as Coordinates | null,
  types: [],
  locationId: '',
  newPlaceId: null,
}

export function EditAddressForm({ userAddress, onSuccess, onClose, onError }: EditAddressFormProps): JSX.Element {

  const config = useConfig()
  const { data: appData = { app: { ...APP_DEFAULT_STATE } } } = useGetAppQuery()
  const [state, setState] = useState<EditAddressFormState>({ ...DEFAULT_STATE })
  const { data: userDetailsData, loading: userDetailsLoading } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [updateUserAddressMutation] = useUpdateUserAddressMutation()
  const [addLocationMutation] = useAddLocationMutation()
  const [updateLocationMutation] = useUpdateLocationMutation()
  const [removeUserAddress] = useRemoveUserAddressMutation()
  const { addToast } = useToasts()
  const [form] = useForm()
  const isMobile = appData.app.deviceType === DeviceTypeEnum.MOBILE

  const _getBuildingTypeEnum = (): BuildingTypeEnum => {
    const rawType = userAddress.location.buildingTypeEnum || 'HOUSE'
    const safeBuildingTypeEnum = Object.values(BuildingTypeEnum).includes(rawType as BuildingTypeEnum)
      ? (rawType as BuildingTypeEnum)
      : BuildingTypeEnum.HOUSE
    return safeBuildingTypeEnum
  }

  const _getCompanyName = (company?: string): string => {
    if (_getBuildingTypeEnum() !== BuildingTypeEnum.OFFICE) return ''
    const value = company?.trim() || userAddress.company?.trim() || ''
    return value
  }

  // Sync state and form fields with userAddress
  useEffect(() => {
    if (userAddress && userAddress.location) {
      const safeBuildingTypeEnum = _getBuildingTypeEnum()
      setState((prevState) => update(prevState, {
        buildingTypeEnum: { $set: safeBuildingTypeEnum },
        locationId: { $set: userAddress.location.id },
      }))
      form.setFieldsValue({
        streetNumber: userAddress.location.streetNumber ?? '',
        streetName: userAddress.location.streetName ?? '',
        locationName: userAddress.location.locationName ?? '',
        alias: userAddress.alias ?? '',
        company: _getCompanyName(),
        buildingTypeEnum: safeBuildingTypeEnum,
        buildingName: userAddress.location.buildingName ?? '',
        unitNumber: userAddress.location.unitNumber ?? '',
        instructions: userAddress.instructions ?? '',
      })
    }
  }, [userAddress, form])

  const setBuildingTypeEnum = (buildingTypeEnum: BuildingTypeEnum): void => {
    setState((prevState) => update(prevState, {
      buildingTypeEnum: { $set: buildingTypeEnum },
    }))
    form.setFieldsValue({ buildingTypeEnum })
  }
  // Allow user to update location via PlaceIdLookup (like AddAddressForm)
  const setLocationLoading = (locationLoading: boolean): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: locationLoading },
    }))
  }

  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const lastPlaceIdRef = React.useRef<string | null>(null)

  // Only set newPlaceId if user selects a new place
  const _handlePlaceSelect = React.useCallback(
    (placeId: string, option: PlaceIdOption): void => {
      if (!placeId || lastPlaceIdRef.current === placeId) {
        return
      }
      setState(prevState => update(prevState, { newPlaceId: { $set: placeId } }))
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        lastPlaceIdRef.current = placeId
        setLocationLoading(true)
        try {
          const addLocationResponse = await addLocationMutation({
            variables: {
              input: {
                placeId,
                types: option.types,
                locationName: option.title.split(',')[0],
              },
            },
          })
          const location = addLocationResponse.data?.addLocation
          if (location) {
            setState((prevState) => update(prevState, {
              locationId: { $set: location.id },
            }))
            form.setFieldsValue({
              locationName: location.locationName,
              streetName: location.streetName,
              streetNumber: location.streetNumber,
            })
          }
        } catch (e) {
          onError(e.message)
        } finally {
          setLocationLoading(false)
        }
      }, 500)
    },
    [addLocationMutation, form, onError, setLocationLoading, state.buildingTypeEnum],
  )

  const setLoading = (loading: boolean): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: loading },
    }))
  }

  const _handleSubmit = async (data: EditAddressFormData): Promise<void> => {
    try {
      setLoading(true)
      let locationIdToUse = state.locationId
      let updatedLocation: LocationFragment | undefined
      if (state.newPlaceId) {
        // This logic is similar to _handlePlaceSelect, but ensures locationId is up to date
        locationIdToUse = state.locationId
      } else {
        // No new place selected, just update existing location
        const mutationInput = {
          buildingTypeEnum: data.buildingTypeEnum || state.buildingTypeEnum,
          buildingName: data.buildingName,
          unitNumber: data.unitNumber,
          streetName: data.streetName,
          locationName: data.locationName,
          streetNumber: data.streetNumber,
          types: state.types,
        }
        const updateLocationResponse = await updateLocationMutation({
          variables: {
            id: state.locationId,
            input: mutationInput,
          },
        })
        updatedLocation = updateLocationResponse?.data?.updateLocation
        locationIdToUse = updatedLocation?.id
        // Sync form and state with updated location data
        if (updatedLocation) {
          const safeBuildingTypeEnum = updatedLocation.buildingTypeEnum || BuildingTypeEnum.HOUSE
          setState((prevState) => update(prevState, {
            buildingTypeEnum: { $set: safeBuildingTypeEnum },
            locationId: { $set: updatedLocation.id },
          }))
          form.setFieldsValue({
            buildingTypeEnum: safeBuildingTypeEnum,
            buildingName: updatedLocation.buildingName,
            unitNumber: updatedLocation.unitNumber,
            streetName: updatedLocation.streetName,
            locationName: updatedLocation.locationName,
            streetNumber: updatedLocation.streetNumber,
          })
        }
      }
      await updateUserAddressMutation({
        variables: {
          id: userAddress?.id,
          input: {
            alias: data.alias,
            instructions: data.instructions,
            company: _getCompanyName(data.company),
            location: locationIdToUse,
            user: userDetailsData?.currentUser?.id,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: SiteHelper.getUserRefetchQueries(),
      })
      setLoading(false)
      onSuccess()
    } catch (e) {
      onError(e.message)
    }
  }

  const _handleChange = (changedFields: FieldData[]) => {
    changedFields.forEach((field) => {
      if (Array.isArray(field.name)) {
        field.name.forEach((name) => {
          if (name === 'buildingTypeEnum') {
            setBuildingTypeEnum(field.value)
          }
        })
      }
    })
  }

  const _handleDeleteModalClose = (): void => {
    setState((prevState) => update(prevState, {
      confirmDeleteModalOpen: { $set: false },
    }))
  }

  const _deleteAddress = (): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: true },
      confirmDeleteModalOpen: { $set: false },
    }))

    try {
      removeUserAddress({
        variables: {
          id: userAddress.id,
        },
        refetchQueries: [{ query: UserDetailsDocument }, { query: UserCartDocument }],
        awaitRefetchQueries: true,
      })
      addToast('Address successfully deleted!', {
        appearance: 'success',
        autoDismiss: true,
      })
      onSuccess()
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }

    setState((prevState) => update(prevState, {
      loading: { $set: false },
    }))
  }

  const _handleSaveAddress = (): void => {
    if (config.isBrowser()) {
      window.sessionStorage.setItem('hasUserEnteredAddressFromModal', 'false')
    }
    form.submit()
    if (onClose) {
      onClose()
    }
  }

  const buildingTypeOptions: IconSelectOption[] = [
    { value: BuildingTypeEnum.HOUSE, icon: IconEnum.HOME, label: 'House' },
    { value: BuildingTypeEnum.OFFICE, icon: IconEnum.OFFICE, label: 'Office' },
    { value: BuildingTypeEnum.APARTMENTS, icon: IconEnum.APARTMENTS, label: 'Apartments' },
    { value: BuildingTypeEnum.COMPLEX, icon: IconEnum.COMPLEX, label: 'Complex' },
  ]

  const loading = userDetailsLoading || state.loading

  return (
    <Container>
      <Spacer universal='16px' />
      <Form
        form={form}
        disabled={loading}
        onFinish={_handleSubmit}
        onFieldsChange={_handleChange}
        className='edit-address-form'
        initialValues={{ buildingTypeEnum: state.buildingTypeEnum }}>
        <Spacer universal='16px' />
        {/* Building type icon row */}
        <Paragraph variant='p3' superBold color={theme.colors.slates.ironsideGrey}>Building type</Paragraph>
        <Spacer universal='12px' />
        {/* Controlled field for buildingTypeEnum */}
        <IconSelectButtonGroup
          options={buildingTypeOptions}
          selected={state.buildingTypeEnum}
          disabled={loading}
          onChange={val => {
            setBuildingTypeEnum(val as BuildingTypeEnum)
            form.setFieldsValue({ buildingTypeEnum: val })
          }}
        />
        {/* Hidden field to ensure buildingTypeEnum is always present in form data */}
        <input
          name='buildingTypeEnum'
          type='hidden'
          value={state.buildingTypeEnum || ''}
          readOnly
        />
        <Spacer universal='8px' />
        <PlaceIdLookup
          onSelect={_handlePlaceSelect}
          resetKey={0}
          disabled={loading}
          isRequired={false}
        />
        <Spacer universal='8px' />
        <TextInput
          name='alias'
          label='Give your address a name'
          placeholder='The main house'
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
          disabled={loading}
        />
        <Spacer universal='12px' />
        {/* Conditionally render address fields based on building type using <If> and <Choose> */}
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.OFFICE}>
          <TextInput
            name='unitNumber'
            label='Suite or floor'
            placeholder='Enter suite or floor'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a suite or floor' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='company'
            label='Business name'
            placeholder='Head office'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a business name' }]} />
        </If>
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.APARTMENTS}>
          <TextInput
            name='unitNumber'
            label='Apartment number'
            placeholder='00'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input an apartment number' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='buildingName'
            label='Flat/Apartment name'
            placeholder='Harbour Lights'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a flat/apartment name' }]} />
        </If>
        <If condition={state.buildingTypeEnum === BuildingTypeEnum.COMPLEX}>
          <TextInput
            label='Unit number'
            name='unitNumber'
            placeholder='00'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a unit number' }]} />
          <Spacer universal='12px' />
          <TextInput
            name='buildingName'
            label='Security estate name'
            placeholder='Harbour Lights'
            disabled={loading}
            rightIcon={IconEnum.CLOSE_CIRCLE}
            clearOnRightIconClick
            rules={[{ required: true, message: 'Please input a security estate name' }]} />
        </If>
        <Spacer universal='12px' />
        <TextInput
          name='streetName'
          label='Street name'
          placeholder='Street name'
          rules={[{ required: true, message: 'Please input a street name' }]}
          disabled={loading}
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
        />
        <Spacer universal='12px' />
        <TextInput
          name='streetNumber'
          label='Street Number'
          placeholder='00'
          disabled={loading}
          rightIcon={IconEnum.CLOSE_CIRCLE}
          clearOnRightIconClick
          rules={[{ required: true, message: 'Please input a street number' }]} />
        <Spacer universal='12px' />
        <TextAreaInput
          label='Delivery note'
          className='instructions'
          name='instructions'
          placeholder='Optional'
          showOptional={false}
          disabled={loading}
        />
        <Spacer universal='24px' />
        <ActionContainer $stickyBottom $variant={isMobile ? 'vertical' : 'horizontal'}>
          <Button
            className='address-action-button'
            variant='primary'
            loading={loading}
            color='black'
            fullWidth={isMobile}
            title='SAVE ADDRESS'
            onClick={_handleSaveAddress} />
          <Spacer mobile='8px' desktop='16px' variant={isMobile ? 'vertical' : 'horizontal'} />
          <Button
            className='address-action-button'
            variant='secondary'
            color='black'
            onClick={() => setState(prev => update(prev, { confirmDeleteModalOpen: { $set: true } }))}
            title='DELETE ADDRESS'
            disabled={loading}
          />
          <Spacer mobile='8px' desktop='16px' variant={isMobile ? 'vertical' : 'horizontal'} />
          <Link variant='l2' decoration='underline' onClick={onClose}> Cancel </Link>
        </ActionContainer>
        <ConfirmDeleteModal
          open={state.confirmDeleteModalOpen}
          title='Delete address'
          action='ADDRESS'
          content='You are about to delete your address! Are your sure you want to proceed?'
          onDelete={_deleteAddress}
          onClose={_handleDeleteModalClose} />
      </Form>
      <Spacer universal='16px' />
    </Container>
  )
}
